import * as Sentry from '@sentry/react-native';
import { logger, consoleTransport, type transportFunctionType } from 'react-native-logs';

function toLogParts(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [value];
}

function serializeLogPart(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value instanceof Error) return value.stack || `${value.name}: ${value.message}`;
  if (value == null || typeof value === 'number' || typeof value === 'boolean') return String(value);

  const seen = new WeakSet<object>();

  try {
    return JSON.stringify(value, (_key, nestedValue) => {
      if (nestedValue instanceof Error) {
        return {
          name: nestedValue.name,
          message: nestedValue.message,
          stack: nestedValue.stack,
        };
      }

      if (typeof nestedValue === 'object' && nestedValue !== null) {
        if (seen.has(nestedValue)) return '[Circular]';
        seen.add(nestedValue);
      }

      return nestedValue;
    });
  } catch {
    return String(value);
  }
}

function extractError(value: unknown): Error | undefined {
  return toLogParts(value).find((entry): entry is Error => entry instanceof Error);
}

function formatLogMessage(value: unknown): string {
  return toLogParts(value).map(serializeLogPart).join(' ');
}

/**
 * Custom transport for Sentry integration.
 * - 'error': Captures as a Sentry exception (if Error object) or message.
 * - 'warn': Added as a Sentry breadcrumb to provide context for potential future errors.
 * - 'debug' / 'info': Ignored by Sentry to save quota and maintain performance.
 */
const sentryTransport: transportFunctionType<object> = (props) => {
  const { msg, level, rawMsg } = props;
  const message = formatLogMessage(msg);

  try {
    // We only send warnings and errors to Sentry
    if (level.text === 'error') {
      const error = extractError(rawMsg) || extractError(msg);

      if (error) {
        Sentry.withScope((scope) => {
          if (message && message !== error.message) {
            scope.setContext('logger', { message });
          }
          Sentry.captureException(error);
        });
      } else if (message) {
        Sentry.captureMessage(message, 'error');
      }
    } else if (level.text === 'warn' && message) {
      Sentry.addBreadcrumb({
        category: 'log',
        message,
        level: 'warning',
      });
    }
  } catch {
    // Never let observability transport become the source of a production error.
  }
};

/**
 * Global application logger using react-native-logs.
 * Configured with console transport for local development
 * and Sentry transport for error/warning tracking.
 */
const logTransports: transportFunctionType<object>[] = [];
if (process.env.NODE_ENV !== 'test') {
  logTransports.push(consoleTransport);
}
if (!__DEV__) {
  logTransports.push(sentryTransport);
}

const log = logger.createLogger({
  levels: {
    debug: 0,
    info: 1,
    warn: 2,
    error: 3,
  },
  severity: __DEV__ ? 'debug' : 'info',
  transport: logTransports,
  transportOptions: {
    colors: {
      info: 'blueBright',
      warn: 'yellowBright',
      error: 'redBright',
    },
  },
  // In tests, disable async transport to avoid deferred timers leaking past test teardown.
  async: process.env.NODE_ENV !== 'test',
  dateFormat: 'time',
  printLevel: true,
  printDate: true,
  enabled: true,
});

export default log;
